import { Button, Table, Popconfirm } from "@/frontend/components/common";
import { prizeTypeService } from "@/frontend/services";
import type { FlatPaginationRequestDTO, PrizeTypeResponseDTO } from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePagination, useDelete } from "@/frontend/hooks";

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7", "10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: FlatPaginationRequestDTO = {
  page: 1,
  pageSize: 7,
};

export function PrizeTypesListPage() {
  const navigate = useNavigate();
  const { data, loading, pagination, loadData } =
    usePagination<PrizeTypeResponseDTO>({
      fetchFn: prizeTypeService.getPaginate,
      initialParams,
      initialPagination,
    });

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } =
    useDelete({
      deleteFn: prizeTypeService.delete,
      loadData,
      successMessage: "Tipo de premiación eliminado exitosamente",
      errorMessage: "Error al eliminar tipo de premiación",
      showConfirm: true,
      confirmTitle: "¿Eliminar tipo de premiación?",
      confirmDescription:
        "Esta acción eliminará el registro. ¿Estás seguro?",
    });

  const columns: ColumnsType<PrizeTypeResponseDTO> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (description: string | null) => description || "-",
    },
    {
      title: "Activo",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (isActive ? "Sí" : "No"),
    },
    {
      title: "Fecha de creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: unknown, record: PrizeTypeResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="default"
            icon={<FaEdit />}
            onClick={() => navigate(`/prize-types/edit/${record.id}`)}
          />
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="default" danger icon={<FaTrash />} />
            </Popconfirm>
          ) : (
            <Button
              type="default"
              danger
              icon={<FaTrash />}
              onClick={() => handleDelete(record.id)}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm w-full">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Tipos de premiación</h2>
          <p className="text-gray-600 mt-1">
            Administra los tipos de premiación disponibles en la plataforma
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          size="large"
          onClick={() => navigate("/prize-types/create")}
        >
          Crear tipo
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        rowKey="id"
        onChange={(pag) => loadData({ page: pag.current, pageSize: pag.pageSize })}
      />
    </div>
  );
}
